import { useMemo, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import { Plus, Search, LayoutGrid, List } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { PageHeader, SelectField, StageBadge, Chip } from "../components/common";
import { useData } from "../context/DataContext";
import { BESOINS, ETAPES, ORIGINES, fmtDate, fmtEUR } from "../lib/constants";

const KanbanCard = ({ d }) => (
  <Link to={`/demandes/${d.id}`} draggable onDragStart={(e) => e.dataTransfer.setData("text/plain", d.id)}
    data-testid={`kanban-card-${d.id}`} className="kc-lift block cursor-grab rounded-lg border border-[var(--kc-line)] bg-white p-4 active:cursor-grabbing">
    <p className="font-medium text-[var(--kc-navy)]">{d.entreprise}</p>
    <p className="mt-0.5 text-xs text-[var(--kc-muted)]">{d.contact_nom}{d.taille && ` · ${d.taille}`}</p>
    <div className="mt-3 flex flex-wrap gap-1">{d.besoins.map((b) => <Chip key={b}>{b}</Chip>)}</div>
    <div className="mt-3 flex justify-between text-xs text-[var(--kc-muted)]"><span>{d.origine}</span><span className="font-mono">{fmtEUR(d.montant_estime)}</span></div>
  </Link>
);

const Kanban = ({ items }) => {
  const { patch } = useData();
  const [over, setOver] = useState(null);
  const drop = async (e, etape) => {
    e.preventDefault(); setOver(null);
    const id = e.dataTransfer.getData("text/plain");
    const d = items.find((x) => x.id === id);
    if (d && d.etape !== etape) { await patch("demandes", id, { etape }); toast.success("Étape mise à jour"); }
  };
  return (
    <div className="grid gap-4 overflow-x-auto pb-4 md:grid-cols-5" data-testid="demandes-kanban">
      {ETAPES.map((e) => {
        const col = items.filter((d) => d.etape === e.id);
        return (
          <div key={e.id} data-testid={`kanban-column-${e.id}`} onDragOver={(ev) => { ev.preventDefault(); setOver(e.id); }} onDragLeave={() => setOver(null)} onDrop={(ev) => drop(ev, e.id)}
            className={`min-w-[220px] rounded-xl border p-3 transition-colors ${over === e.id ? "border-[var(--kc-gold)] bg-[var(--kc-gold)]/5" : "border-transparent bg-[var(--kc-sand)]/60"}`}>
            <div className="mb-3 flex items-center justify-between px-1">
              <span className="flex items-center gap-2 text-sm font-semibold text-[var(--kc-navy)]"><span className="h-2 w-2 rounded-full" style={{ background: e.color }} />{e.label}</span>
              <span className="font-mono text-xs text-[var(--kc-muted)]" data-testid={`kanban-count-${e.id}`}>{col.length}</span>
            </div>
            <div className="space-y-2.5">{col.map((d) => <KanbanCard key={d.id} d={d} />)}</div>
          </div>
        );
      })}
    </div>
  );
};

const ListView = ({ items }) => {
  const navigate = useNavigate();
  return (
    <div className="kc-card overflow-hidden" data-testid="demandes-table">
      <Table>
        <TableHeader><TableRow>
          <TableHead>Entreprise</TableHead><TableHead>Contact</TableHead><TableHead>Besoin</TableHead><TableHead>Origine</TableHead><TableHead>Étape</TableHead><TableHead className="text-right">Montant</TableHead><TableHead>Reçue le</TableHead>
        </TableRow></TableHeader>
        <TableBody>
          {items.map((d) => (
            <TableRow key={d.id} className="cursor-pointer" onClick={() => navigate(`/demandes/${d.id}`)} data-testid={`demande-row-${d.id}`}>
              <TableCell className="font-medium text-[var(--kc-navy)]">{d.entreprise}<p className="text-xs font-normal text-[var(--kc-muted)]">{d.taille}</p></TableCell>
              <TableCell>{d.contact_nom}<p className="text-xs text-[var(--kc-muted)]">{d.contact_email}</p></TableCell>
              <TableCell><div className="flex flex-wrap gap-1">{d.besoins.map((b) => <Chip key={b}>{b}</Chip>)}</div></TableCell>
              <TableCell className="text-sm">{d.origine}</TableCell>
              <TableCell><StageBadge etape={d.etape} /></TableCell>
              <TableCell className="text-right font-mono text-sm">{fmtEUR(d.montant_estime)}</TableCell>
              <TableCell className="text-sm text-[var(--kc-muted)]">{fmtDate(d.created_at)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {!items.length && <p className="p-8 text-center text-sm text-[var(--kc-muted)]">Aucune demande ne correspond aux filtres.</p>}
    </div>
  );
};

export default function Demandes() {
  const { demandes } = useData();
  const { openNewDemande } = useOutletContext();
  const [view, setView] = useState("kanban");
  const [q, setQ] = useState("");
  const [besoin, setBesoin] = useState("all");
  const [origine, setOrigine] = useState("all");

  const items = useMemo(() => demandes.filter((d) =>
    (besoin === "all" || d.besoins.includes(besoin)) && (origine === "all" || d.origine === origine) &&
    `${d.entreprise} ${d.contact_nom} ${d.secteur}`.toLowerCase().includes(q.toLowerCase())), [demandes, q, besoin, origine]);

  const viewBtn = (v, Icon, label) => (
    <button onClick={() => setView(v)} data-testid={`view-${v}-button`} className={`flex items-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors ${view === v ? "bg-white text-[var(--kc-navy)] shadow-sm" : "text-[var(--kc-muted)]"}`}><Icon className="h-4 w-4" />{label}</button>
  );

  return (
    <div data-testid="demandes-page">
      <PageHeader eyebrow="Pipeline commercial" title="Demandes" description="Glissez une carte d'une colonne à l'autre pour faire avancer la demande."
        actions={<Button onClick={openNewDemande} className="kc-btn-primary" data-testid="demandes-new-button"><Plus className="mr-1 h-4 w-4" />Nouvelle demande</Button>} />
      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center">
        <div className="relative flex-1"><Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--kc-muted)]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une entreprise, un contact…" className="bg-white pl-9" data-testid="search-demandes-input" /></div>
        <div className="w-full lg:w-52"><SelectField testId="filter-besoin-select" value={besoin} onChange={setBesoin} options={[{ id: "all", label: "Tous les besoins" }, ...BESOINS]} /></div>
        <div className="w-full lg:w-52"><SelectField testId="filter-origine-select" value={origine} onChange={setOrigine} options={[{ id: "all", label: "Toutes les origines" }, ...ORIGINES]} /></div>
        <div className="flex rounded-lg bg-[var(--kc-sand)] p-1">{viewBtn("kanban", LayoutGrid, "Kanban")}{viewBtn("liste", List, "Liste")}</div>
      </div>
      {view === "kanban" ? <Kanban items={items} /> : <ListView items={items} />}
    </div>
  );
}
